import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

/**
 * PublishConfirmModal
 *
 * Props:
 *  open       {bool}
 *  slug       {string}
 *  saving     {bool}
 *  onClose    {fn}
 *  onConfirm  {fn}   — calls actual publish
 */
const PublishConfirmModal = ({ open, slug, saving, onClose, onConfirm }) => {
  const [published, setPublished] = useState(false);
  const siteUrl = process.env.REACT_APP_SITE_URL || "https://softheight.com";
  const liveUrl = `${siteUrl}/${slug}`;

  const handleConfirm = async () => {
    await onConfirm();
    setPublished(true);
  };

  const handleClose = () => {
    setPublished(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      PaperProps={{
        sx: {
          borderRadius: "14px",
          background: "#13161f",
          border: "1px solid rgba(255,255,255,0.08)",
          minWidth: 420,
          p: 0,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {!published ? (
          /* ── Confirmation screen ── */
          <Box p={3.5}>
            <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
              Publish this page?
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              This will make your page publicly accessible at:
            </Typography>

            <Box
              sx={{
                background: "rgba(59,130,246,0.08)",
                border: "1px solid rgba(59,130,246,0.2)",
                borderRadius: "8px",
                px: 2,
                py: 1.25,
                mb: 3,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "#3b82f6",
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                color="#60a5fa"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {liveUrl}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={1.5}>
              <Button
                onClick={handleClose}
                disabled={saving}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  color: "text.secondary",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  px: 2,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={13} color="inherit" />
                  ) : null
                }
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  borderRadius: "8px",
                  background: "#2e7d32",
                  px: 2.5,
                  "&:hover": { background: "#1b5e20" },
                }}
              >
                {saving ? "Publishing..." : "Yes, publish"}
              </Button>
            </Box>
          </Box>
        ) : (
          /* ── Success screen ── */
          <Box p={3.5} display="flex" flexDirection="column" alignItems="center" textAlign="center">
            <CheckCircleOutlineIcon
              sx={{ fontSize: 48, color: "#4ade80", mb: 2 }}
            />
            <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
              Page is live!
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Your page is now publicly accessible.
            </Typography>

            <Box
              sx={{
                background: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.2)",
                borderRadius: "8px",
                px: 2,
                py: 1.25,
                mb: 3,
                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                color="#4ade80"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {liveUrl}
              </Typography>
            </Box>

            <Box display="flex" gap={1.5}>
              <Button
                onClick={handleClose}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  color: "text.secondary",
                  borderRadius: "8px",
                  border: "1px solid rgba(255,255,255,0.1)",
                  px: 2,
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => window.open(liveUrl, "_blank")}
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  borderRadius: "8px",
                  background: "#2e7d32",
                  px: 2.5,
                  "&:hover": { background: "#1b5e20" },
                }}
              >
                View live page
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublishConfirmModal;